import { useId } from 'react';

import { Box, Button, Flex, Grid, Skeleton, useSlotRecipe } from '@chakra-ui/react';

import isEqual from 'lodash/isEqual';

import { IconsLinear } from '@nocowanie/icons';

import { SearchEngineDesktopV7Props } from './search-engine-desktop-v7.props';

import {
    GuestsPicker,
    RangePicker,
    RangePickerDates,
    SearchEngineInstantReservationCheckbox,
    SearchLocation,
} from '../../../components';
import { defaultSearchEngineTranslation, GuestsPickerInputDataModel } from '../../../models';
import { AutocompleteBaseItemModel } from '../../molecules/autocomplete/autocomplete-base-item.model';

export const SearchEngineDesktopV7 = <T extends AutocompleteBaseItemModel>({
    setValue,
    getValues,
    register,
    watch,
    isSubmitted,
    onInputValueUpdated,
    translationData = defaultSearchEngineTranslation,
    showInstantReservationInput = true,
    autocompleteData,
    isWebComponentVersion = false,
    isMapSearch = false,
    showSkeleton = false,
    ...props
}: SearchEngineDesktopV7Props<T>) => {
    const recipe = useSlotRecipe({
        key: 'searchEngineDesktop',
    });
    const styles = recipe();
    const locationId = useId();

    const { isLoading } = autocompleteData;

    return (
        <Flex flexDirection={'column'}>
            <Skeleton loading={showSkeleton} asChild>
                <Grid
                    className="search-engine-container"
                    css={isMapSearch ? { ...styles.mapSearchWrapper } : { ...styles.wrapper }}
                    {...props}
                >
                    <SearchLocation
                        inputProps={{
                            id: locationId,
                            inputProps: {
                                ...register('location'),
                                css: styles.locationInput,
                                'data-testid': 'search-engine-location-input',
                            },
                        }}
                        autocompleteData={autocompleteData}
                        flexGrow={4}
                    />

                    <Box css={styles.separator} />

                    <Box flexGrow={3}>
                        <RangePicker
                            selectedDates={[
                                getValues('dates.checkInDate'),
                                getValues('dates.checkOutDate'),
                            ]}
                            onRangeChange={(dates: RangePickerDates) => {
                                if (
                                    dates[0] !== getValues('dates.checkInDate') ||
                                    dates[1] !== getValues('dates.checkOutDate')
                                ) {
                                    setValue('dates.checkInDate', dates[0]);
                                    setValue('dates.checkOutDate', dates[1]);

                                    onInputValueUpdated();
                                }
                            }}
                            inputProps={{
                                inputProps: {
                                    border: 'none',
                                    borderRadius: 0,
                                    h: 'var(--element-height)',
                                },
                                'data-testid': 'search-engine-range-picker',
                            }}
                            isMobileBrowser={false}
                            isWebComponent={isWebComponentVersion}
                            flexDates={{
                                withFlexDates: true,
                                onFlexDateSelect: (flexDays: number) => {
                                    setValue('flexDates', flexDays);
                                    onInputValueUpdated();
                                },
                                selectedFlexDays: watch('flexDates'),
                            }}
                        />
                    </Box>

                    <Box css={styles.separator} />

                    <Box flexGrow={3}>
                        <GuestsPicker
                            inputData={getValues('guests')}
                            noArrow={true}
                            onInputDataChanged={(data: GuestsPickerInputDataModel) => {
                                const newGuests = {
                                    adultsCount: data.adultsCount || 1,
                                    childrenCount: data.childrenCount || 0,
                                    childrenAges: data.childrenAges
                                        ? data.childrenAges.filter(
                                              (age): age is number => age !== undefined,
                                          )
                                        : [],
                                };

                                if (!isEqual(newGuests, getValues('guests'))) {
                                    setValue('guests', newGuests);
                                    onInputValueUpdated();
                                }
                            }}
                            triggerProps={{
                                'data-testid': 'search-engine-guests-picker',
                                css: styles.guestPickerTrigger,
                            }}
                            isMobileBrowser={false}
                        />
                    </Box>

                    <Button
                        type="submit"
                        disabled={isLoading || isSubmitted}
                        variant={'solid'}
                        colorPalette={'brand'}
                        data-testid="search-engine-submit-button"
                        css={styles.submit}
                    >
                        <IconsLinear.Search fontSize={'xl'} />
                        {translationData.buttonSubmitLabel}
                    </Button>
                </Grid>
            </Skeleton>

            {showInstantReservationInput && !isMapSearch ? (
                <SearchEngineInstantReservationCheckbox
                    onInputValueUpdated={onInputValueUpdated}
                    setValue={setValue}
                    watch={watch}
                    withBackground={true}
                    css={styles.instantReservationCheckboxWrapper}
                />
            ) : null}
        </Flex>
    );
};
